/**
 * C3 — consumer SPA package components.
 *
 * Three surfaces:
 *   1. BuyPackageHeaderLink — passive header chip; opens the PackageCheckoutSheet
 *   2. PackageCheckoutSheet — tier picker + e-invoice + consent → Stripe redirect
 *   3. RedeemPackPicker — review step; shows the customer's active packs and
 *      lets them apply one to the current booking
 *
 * Shared sessions.ts mirror of backend sessions_for_boarding ensures the SPA
 * never shows "1 session needed" when the backend will actually charge 5.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from './api/client'
import type {
  AddOnOffering,
  MyPackagePublic,
  MyPetPublic,
  PackageTierOffering,
  ServicePackageOffering,
} from './api/client'

// ---------------------------------------------------------------------------
// Shared math — mirrors backend packages_redemption_service.sessions_for_boarding
// ---------------------------------------------------------------------------

export function nightsNeeded(
  serviceType: 'boarding' | 'daycare' | 'grooming',
  checkIn?: string,
  checkOut?: string,
): number {
  if (serviceType === 'daycare') return 1
  if (serviceType === 'grooming') return 1
  if (!checkIn || !checkOut) return 1
  const days = Math.floor(
    (new Date(checkOut).getTime() - new Date(checkIn).getTime()) / 86_400_000,
  )
  return Math.max(1, days)
}

/** True when the active locale is English — used to pick backend-supplied
 *  name_en/description_en over the zh-Hant primary. */
function isEnglish(lang: string): boolean {
  return lang.toLowerCase().startsWith('en')
}

function fmtTwd(minor: number): string {
  try {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      maximumFractionDigits: 0,
    }).format(minor / 100)
  } catch {
    return `NT$${minor / 100}`
  }
}

/** ECPay's AIO cashier is reached by POSTing a signed form — build it, submit,
 * and let the browser navigate to the hosted payment page. */
function submitEcpayForm(action: string, fields: Record<string, string>): void {
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = action
  form.style.display = 'none'
  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    form.appendChild(input)
  }
  document.body.appendChild(form)
  form.submit()
}

// ---------------------------------------------------------------------------
// Post-payment reconcile stash
//
// The Stripe/ECPay success redirect comes back to the booking page carrying
// only `?packages_status=success` — NOT the session id / MerchantTradeNo the
// reconcile endpoints need. So just before we hand the browser off to the
// cashier we stash the provider + its id in sessionStorage (survives the
// same-tab navigation); the return handler reads it back to confirm the
// purchase actually settled instead of trusting the redirect alone.
// ---------------------------------------------------------------------------

const RECONCILE_KEY = 'petpro_pkg_reconcile'

type ReconcileStash =
  | { provider: 'stripe'; slug: string; session_id: string }
  | { provider: 'ecpay'; slug: string; merchant_trade_no: string }

function stashReconcile(stash: ReconcileStash): void {
  try {
    sessionStorage.setItem(RECONCILE_KEY, JSON.stringify(stash))
  } catch {
    // Private-mode / storage-disabled: the server-to-server webhook still
    // activates the purchase, so this is a best-effort confirmation only.
  }
}

function readReconcile(): ReconcileStash | null {
  try {
    const raw = sessionStorage.getItem(RECONCILE_KEY)
    return raw ? (JSON.parse(raw) as ReconcileStash) : null
  } catch {
    return null
  }
}

function clearReconcile(): void {
  try {
    sessionStorage.removeItem(RECONCILE_KEY)
  } catch {
    /* ignore */
  }
}

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

// ---------------------------------------------------------------------------
// PackageTierCard
// ---------------------------------------------------------------------------

interface PackageTierCardProps {
  pkg: ServicePackageOffering
  tier: PackageTierOffering
  onBuy: () => void
}

function PackageTierCard({ pkg, tier, onBuy }: PackageTierCardProps) {
  const { t, i18n } = useTranslation()
  const en = isEnglish(i18n.language)
  const totalSessions = tier.paid_sessions + tier.bonus_sessions
  const name = en ? pkg.name_en || pkg.name_zh : pkg.name_zh
  const description = en
    ? pkg.description_en || pkg.description_zh
    : pkg.description_zh
  return (
    <div
      className="card stack"
      data-testid={`package-tier-card-${pkg.id}-${tier.id}`}
    >
      <div>
        <h3>{name}</h3>
        {description && <p className="muted">{description}</p>}
      </div>
      <div className="row-between">
        <span className="price">{fmtTwd(tier.price_minor)}</span>
        <span className="muted">
          {t('packages.sessions', { n: totalSessions })}
          {tier.bonus_sessions > 0
            ? t('packages.bonusSuffix', { n: tier.bonus_sessions })
            : ''}
        </span>
      </div>
      <div>
        {pkg.validity_days && (
          <p className="muted">
            {t('packages.validity', { n: pkg.validity_days })}
          </p>
        )}
        <p className="muted">
          {pkg.transferability === 'household'
            ? t('packages.transferHousehold')
            : t('packages.transferOwnerOnly')}
        </p>
      </div>
      <button
        type="button"
        className="btn btn-primary"
        onClick={onBuy}
        data-testid="buy-package-tier"
      >
        {t('packages.buy')}
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// PackageCheckoutSheet — buy flow
// ---------------------------------------------------------------------------

interface CheckoutSheetProps {
  slug: string
  bookingToken: string | null
  pkg: ServicePackageOffering
  tier: PackageTierOffering
  onClose: () => void
}

export function PackageCheckoutSheet({
  slug,
  bookingToken,
  pkg,
  tier,
  onClose,
}: CheckoutSheetProps) {
  const { t, i18n } = useTranslation()
  const en = isEnglish(i18n.language)
  const pkgName = en ? pkg.name_en || pkg.name_zh : pkg.name_zh
  const [consent, setConsent] = useState(false)
  const [einvoiceCarrier, setEinvoiceCarrier] = useState('')
  const [einvoiceTaxId, setEinvoiceTaxId] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // owner_only packages redeem for ONE pet, chosen now and frozen onto the
  // purchase. '__new__' means "a pet not on file yet" — the checkout accepts
  // a name and creates it, so a first-time buyer isn't locked out.
  const requiresPet = pkg.transferability === 'owner_only'
  const [pets, setPets] = useState<MyPetPublic[] | null>(null)
  const [petId, setPetId] = useState<string>('')
  const [newPetName, setNewPetName] = useState('')

  useEffect(() => {
    if (!requiresPet || !bookingToken) return
    let cancelled = false
    api
      .listMyPets(slug, bookingToken)
      .then((r) => {
        if (cancelled) return
        setPets(r.pets)
        // Pre-select when there's exactly one — the common case, and it
        // saves a tap without hiding the choice.
        if (r.pets.length === 1) setPetId(r.pets[0].id)
        else if (r.pets.length === 0) setPetId('__new__')
      })
      .catch(() => {
        if (!cancelled) setPets([])
      })
    return () => {
      cancelled = true
    }
  }, [requiresPet, bookingToken, slug])

  const requiresEinvoice = pkg.einvoice_required

  async function buy() {
    if (!bookingToken) {
      setError(t('packages.errNeedVerify'))
      return
    }
    if (!consent) {
      setError(t('packages.errNeedConsent'))
      return
    }
    if (requiresEinvoice && !einvoiceCarrier && !einvoiceTaxId) {
      setError(t('packages.errNeedEinvoice'))
      return
    }
    if (requiresPet && !petId) {
      setError(t('packages.errNeedPet'))
      return
    }
    if (requiresPet && petId === '__new__' && !newPetName.trim()) {
      setError(t('packages.errNeedPetName'))
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const successUrl = `${window.location.origin}${window.location.pathname}?packages_status=success`
      const cancelUrl = `${window.location.origin}${window.location.pathname}?packages_status=cancel`
      const resp = await api.createPackagesCheckout(slug, {
        booking_token: bookingToken,
        package_id: pkg.id,
        tier_id: tier.id,
        success_url: successUrl,
        cancel_url: cancelUrl,
        consent_accepted: true,
        einvoice_carrier: einvoiceCarrier || undefined,
        einvoice_tax_id: einvoiceTaxId || undefined,
        ...(requiresPet
          ? petId === '__new__'
            ? { pet_name: newPetName.trim() }
            : { pet_id: petId }
          : {}),
      })
      if ('form_action' in resp) {
        // ECPay: browser POSTs the signed order to the hosted cashier.
        stashReconcile({
          provider: 'ecpay',
          slug,
          merchant_trade_no: resp.form_fields.MerchantTradeNo,
        })
        submitEcpayForm(resp.form_action, resp.form_fields)
      } else {
        stashReconcile({ provider: 'stripe', slug, session_id: resp.session_id })
        window.location.href = resp.url
      }
    } catch (e) {
      const detail = (e as { detail?: string; message?: string })?.detail
      setError(detail ?? t('packages.errCheckoutFailed'))
      setSubmitting(false)
    }
  }

  return (
    <div
      className="sheet-overlay"
      onClick={onClose}
      data-testid="package-checkout-sheet"
    >
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <h2>{t('packages.sheetTitle', { name: pkgName })}</h2>
        <p className="muted">
          {t('packages.sheetSummary', {
            n: tier.paid_sessions + tier.bonus_sessions,
            price: fmtTwd(tier.price_minor),
          })}
        </p>

        {/* Refund policy summary — Article 19 defense, surfaced FIRST */}
        <div className="panel" data-testid="refund-policy-summary">
          {pkg.refund_policy_mode === 'none' && t('packages.refundNone')}
          {pkg.refund_policy_mode === 'prorated' && t('packages.refundProrated')}
          {pkg.refund_policy_mode === 'prorated_with_fee' &&
            t('packages.refundProratedWithFee')}
          {pkg.refund_policy_mode === 'full_within_window' &&
            t('packages.refundFullWithinWindow')}
          <div className="muted small">{t('packages.einvoiceNotice')}</div>
        </div>

        {requiresPet && (
          <div className="field-group" data-testid="package-pet-picker">
            <label className="field">{t('packages.petLabel')}</label>
            <p className="muted">{t('packages.petHint')}</p>
            {!bookingToken ? (
              // No token yet => we can't list this owner's pets. Say so
              // instead of spinning forever on a load that never starts.
              <p className="muted">{t('packages.petNeedVerify')}</p>
            ) : pets === null ? (
              <p className="muted">{t('loading')}</p>
            ) : (
              <div>
                {pets.map((p) => (
                  <label
                    key={p.id}
                    className={`choice${petId === p.id ? ' selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="package-pet"
                      value={p.id}
                      checked={petId === p.id}
                      onChange={() => setPetId(p.id)}
                      data-testid={`pet-option-${p.id}`}
                    />
                    <span>{p.pet_name}</span>
                    {p.breed && <span className="muted">{p.breed}</span>}
                  </label>
                ))}
                <label
                  className={`choice${petId === '__new__' ? ' selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="package-pet"
                    value="__new__"
                    checked={petId === '__new__'}
                    onChange={() => setPetId('__new__')}
                    data-testid="pet-option-new"
                  />
                  <span>
                    {pets.length
                      ? t('packages.petAddMore')
                      : t('packages.petAddFirst')}
                  </span>
                </label>
                {petId === '__new__' && (
                  <input
                    type="text"
                    value={newPetName}
                    onChange={(e) => setNewPetName(e.target.value)}
                    placeholder={t('packages.petNamePlaceholder')}
                    maxLength={100}
                    data-testid="new-pet-name"
                  />
                )}
              </div>
            )}
          </div>
        )}

        {requiresEinvoice && (
          <div className="stack field-group">
            <div>
              <label className="field">
                {t('packages.einvoiceCarrierLabel')}
              </label>
              <input
                type="text"
                value={einvoiceCarrier}
                onChange={(e) => setEinvoiceCarrier(e.target.value)}
                placeholder="/AB12345"
              />
            </div>
            <div>
              <label className="field">{t('packages.einvoiceTaxIdLabel')}</label>
              <input
                type="text"
                value={einvoiceTaxId}
                onChange={(e) => setEinvoiceTaxId(e.target.value)}
                placeholder="12345678"
                maxLength={8}
              />
            </div>
          </div>
        )}

        <label className="choice choice-start">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            data-testid="consent-checkbox"
          />
          <span>{t('packages.consent')}</span>
        </label>

        {error && (
          <p className="text-danger" data-testid="checkout-error">
            {error}
          </p>
        )}

        <div className="btn-row">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="btn btn-ghost"
          >
            {t('packages.cancel')}
          </button>
          <button
            type="button"
            onClick={buy}
            disabled={submitting}
            className="btn btn-primary"
            data-testid="checkout-confirm"
          >
            {submitting
              ? t('packages.paying')
              : t('packages.pay', { price: fmtTwd(tier.price_minor) })}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// PackageCheckoutReturn — post-payment confirmation
//
// Rendered once by BookingPage. When the Stripe/ECPay cashier redirects the
// browser back with `?packages_status=success|cancel`, this reads the stash
// left by buy(), reconciles against the backend (the webhook may not have
// landed yet, so we poll a few times), and shows the outcome. Renders nothing
// when there is no packages_status on the URL.
// ---------------------------------------------------------------------------

type ReturnState =
  | { kind: 'confirming' }
  | { kind: 'success'; purchase: MyPackagePublic | null }
  | { kind: 'pending' }
  | { kind: 'cancel' }

const _SETTLED_STATUSES = ['paid', 'activated', 'already_processed']

export function PackageCheckoutReturn({ slug }: { slug: string }) {
  const { t, i18n } = useTranslation()
  const [state, setState] = useState<ReturnState | null>(null)
  // Run the return handler exactly once per real page load. StrictMode invokes
  // effects twice in dev (setup → cleanup → setup); without this guard the
  // first pass would strip the URL marker so the second pass reads nothing, and
  // a cleanup-driven cancel would strand the in-flight reconcile at
  // "confirming". A real cashier redirect is a full page load, so the ref
  // resets naturally for each genuine return.
  const handledRef = useRef(false)

  useEffect(() => {
    if (handledRef.current) return
    const params = new URLSearchParams(window.location.search)
    const status = params.get('packages_status')
    if (!status) return
    handledRef.current = true

    // Strip the marker so a refresh (or back button) doesn't replay this.
    params.delete('packages_status')
    const query = params.toString()
    window.history.replaceState(
      null,
      '',
      window.location.pathname + (query ? `?${query}` : ''),
    )

    if (status === 'cancel') {
      clearReconcile()
      setState({ kind: 'cancel' })
      return
    }

    const stash = readReconcile()
    if (!stash || stash.slug !== slug) {
      // No id to reconcile with (storage cleared, cross-tab, or a stale link).
      // The server-to-server webhook still settles it, so show a soft success.
      clearReconcile()
      setState({ kind: 'success', purchase: null })
      return
    }

    setState({ kind: 'confirming' })
    void (async () => {
      // The webhook usually beats the redirect, but not always — retry a few
      // times before falling back to "we're still processing". No unmount guard:
      // the handler runs once and setState is a no-op after unmount in React 18.
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const r =
            stash.provider === 'ecpay'
              ? await api.reconcilePackagesEcpay(slug, stash.merchant_trade_no)
              : await api.reconcilePackagesCheckout(slug, stash.session_id)
          if (_SETTLED_STATUSES.includes(r.status)) {
            clearReconcile()
            setState({ kind: 'success', purchase: r.purchase })
            return
          }
        } catch {
          // 404 (id not found yet) or network — fall through and retry.
        }
        if (attempt < 2) await sleep(1500)
      }
      clearReconcile()
      setState({ kind: 'pending' })
    })()
  }, [slug])

  if (!state) return null

  const close = () => setState(null)

  return (
    <div
      className="sheet-overlay"
      onClick={close}
      data-testid="package-checkout-return"
    >
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        {state.kind === 'confirming' && (
          <>
            <h2>{t('packages.returnConfirmingTitle')}</h2>
            <p className="muted">{t('packages.returnConfirmingBody')}</p>
          </>
        )}
        {state.kind === 'success' && (
          <>
            <h2>{t('packages.returnSuccessTitle')}</h2>
            {state.purchase ? (
              <p>
                {state.purchase.expires_at
                  ? t('packages.returnSuccessBodyExpiry', {
                      name: isEnglish(i18n.language)
                        ? state.purchase.name_en || state.purchase.name_zh
                        : state.purchase.name_zh,
                      n: state.purchase.sessions_total,
                      date: state.purchase.expires_at.slice(0, 10),
                    })
                  : t('packages.returnSuccessBody', {
                      name: isEnglish(i18n.language)
                        ? state.purchase.name_en || state.purchase.name_zh
                        : state.purchase.name_zh,
                      n: state.purchase.sessions_total,
                    })}
              </p>
            ) : (
              <p>{t('packages.returnSuccessGeneric')}</p>
            )}
          </>
        )}
        {state.kind === 'pending' && (
          <>
            <h2>{t('packages.returnPendingTitle')}</h2>
            <p className="muted">{t('packages.returnPendingBody')}</p>
          </>
        )}
        {state.kind === 'cancel' && (
          <>
            <h2>{t('packages.returnCancelTitle')}</h2>
            <p className="muted">{t('packages.returnCancelBody')}</p>
          </>
        )}
        <div className="actions">
          <button className="btn-primary" onClick={close}>
            {t('packages.close')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// BuyPackageHeaderLink — passive entry point shown in BookingPage header
// ---------------------------------------------------------------------------

interface HeaderLinkProps {
  slug: string
  bookingToken: string | null
}

export function BuyPackageHeaderLink({ slug, bookingToken }: HeaderLinkProps) {
  const { t } = useTranslation()
  const [packages, setPackages] = useState<ServicePackageOffering[] | null>(null)
  const [open, setOpen] = useState(false)
  const [activeTier, setActiveTier] = useState<{
    pkg: ServicePackageOffering
    tier: PackageTierOffering
  } | null>(null)

  useEffect(() => {
    let alive = true
    api
      .listPackagesCatalog(slug)
      .then((r) => alive && setPackages(r.packages))
      .catch(() => alive && setPackages([]))
    return () => {
      alive = false
    }
  }, [slug])

  if (!packages || packages.length === 0) return null

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="chip"
        data-testid="header-buy-packages"
      >
        {t('packages.headerCta', { n: packages.length })}
      </button>
      {open && (
        <div className="sheet-overlay" onClick={() => setOpen(false)}>
          <div
            className="sheet sheet-wide"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="row-between">
              <h2>{t('packages.catalogTitle')}</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="link-btn"
              >
                {t('packages.close')}
              </button>
            </div>
            <div className="grid-2">
              {packages.flatMap((p) =>
                p.tiers.map((tier) => (
                  <PackageTierCard
                    key={`${p.id}-${tier.id}`}
                    pkg={p}
                    tier={tier}
                    onBuy={() => {
                      setActiveTier({ pkg: p, tier })
                      setOpen(false)
                    }}
                  />
                )),
              )}
            </div>
          </div>
        </div>
      )}
      {activeTier && (
        <PackageCheckoutSheet
          slug={slug}
          bookingToken={bookingToken}
          pkg={activeTier.pkg}
          tier={activeTier.tier}
          onClose={() => setActiveTier(null)}
        />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// RedeemPackPicker — review-step component
// ---------------------------------------------------------------------------

interface RedeemPickerProps {
  slug: string
  bookingToken: string | null
  serviceType: 'boarding' | 'daycare' | 'grooming'
  nightsNeeded: number
  selected: string | null
  onChange: (purchaseId: string | null) => void
}

export function RedeemPackPicker({
  slug,
  bookingToken,
  serviceType,
  nightsNeeded: needed,
  selected,
  onChange,
}: RedeemPickerProps) {
  const { t, i18n } = useTranslation()
  const en = isEnglish(i18n.language)
  const [packs, setPacks] = useState<MyPackagePublic[] | null>(null)

  useEffect(() => {
    if (!bookingToken) {
      setPacks([])
      return
    }
    let alive = true
    api
      .listMyPackages(slug, bookingToken, serviceType)
      .then((r) => alive && setPacks(r.purchases))
      .catch(() => alive && setPacks([]))
    return () => {
      alive = false
    }
  }, [slug, bookingToken, serviceType])

  const eligible = useMemo(
    () => (packs ?? []).filter((p) => p.sessions_remaining >= needed),
    [packs, needed],
  )

  if (!packs || packs.length === 0) return null

  return (
    <div className="panel panel-accent" data-testid="redeem-picker">
      <h3>{t('packages.redeemTitle')}</h3>
      <p className="muted">{t('packages.redeemSubtitle', { n: needed })}</p>
      <div>
        <label className={`choice${selected === null ? ' selected' : ''}`}>
          <input
            type="radio"
            checked={selected === null}
            onChange={() => onChange(null)}
            data-testid="redeem-none"
          />
          <span>{t('packages.redeemNone')}</span>
        </label>
        {packs.map((p) => {
          const insufficient = p.sessions_remaining < needed
          return (
            <label
              key={p.id}
              className={`choice${insufficient ? ' disabled' : ''}${
                selected === p.id ? ' selected' : ''
              }`}
            >
              <input
                type="radio"
                checked={selected === p.id}
                onChange={() => !insufficient && onChange(p.id)}
                disabled={insufficient}
                data-testid={`redeem-${p.id}`}
              />
              <span>
                {t('packages.redeemOption', {
                  name: en ? p.name_en || p.name_zh : p.name_zh,
                  remaining: p.sessions_remaining,
                  total: p.sessions_total,
                })}
                {insufficient && t('packages.redeemInsufficientTag')}
              </span>
            </label>
          )
        })}
      </div>
      {eligible.length === 0 && (
        <p className="muted small">{t('packages.redeemInsufficientHint')}</p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// AddOnPicker — ADDONS-4
// ---------------------------------------------------------------------------

export interface AddOnSelection {
  serviceType: 'boarding' | 'daycare' | 'grooming'
  id: string
  quantity: number
  price_minor: number
  name_zh: string
}

interface AddOnLeg {
  serviceType: 'boarding' | 'daycare' | 'grooming'
  /** Header shown above this leg's add-ons (e.g. "寄宿加購"). Omit for
   *  single-leg bookings to render a flat list with no header. */
  label?: string
}

interface AddOnPickerProps {
  slug: string
  legs: AddOnLeg[]
  selections: AddOnSelection[]
  onChange: (next: AddOnSelection[]) => void
}

export function AddOnPicker({
  slug,
  legs,
  selections,
  onChange,
}: AddOnPickerProps) {
  const { t, i18n } = useTranslation()
  const en = isEnglish(i18n.language)
  // catalog keyed by serviceType
  const [catalog, setCatalog] = useState<
    Record<string, AddOnOffering[]> | null
  >(null)

  // Stable key for the legs we're fetching (avoids refetch churn).
  const legKey = legs.map((l) => l.serviceType).join(',')

  useEffect(() => {
    let alive = true
    const types = legKey ? legKey.split(',') : []
    Promise.all(
      types.map((st) =>
        api
          .listAddOns(slug, st as AddOnLeg['serviceType'])
          .then((rows) => [st, rows] as const)
          .catch(() => [st, [] as AddOnOffering[]] as const),
      ),
    ).then((pairs) => {
      if (!alive) return
      const map: Record<string, AddOnOffering[]> = {}
      for (const [st, rows] of pairs) map[st] = rows
      setCatalog(map)
    })
    return () => {
      alive = false
    }
  }, [slug, legKey])

  if (!catalog) return null
  const anyAddons = Object.values(catalog).some((rows) => rows.length > 0)
  if (!anyAddons) return null

  function qtyFor(st: string, id: string): number {
    return (
      selections.find((s) => s.serviceType === st && s.id === id)?.quantity ?? 0
    )
  }

  function setQty(
    st: AddOnLeg['serviceType'],
    addon: AddOnOffering,
    qty: number,
  ) {
    const next = selections.filter(
      (s) => !(s.serviceType === st && s.id === addon.id),
    )
    if (qty > 0) {
      next.push({
        serviceType: st,
        id: addon.id,
        quantity: qty,
        price_minor: addon.price_minor,
        name_zh: addon.name_zh,
      })
    }
    onChange(next)
  }

  return (
    <div className="panel" data-testid="addon-picker">
      <h3>{t('packages.addonTitle')}</h3>
      {legs.map((leg) => {
        const rows = catalog[leg.serviceType] ?? []
        if (rows.length === 0) return null
        return (
          <div key={leg.serviceType}>
            {leg.label && <label className="field">{leg.label}</label>}
            <div>
              {rows.map((addon) => {
                const qty = qtyFor(leg.serviceType, addon.id)
                const checked = qty > 0
                return (
                  <div
                    className={`choice${checked ? ' selected' : ''}`}
                    key={addon.id}
                    data-testid={`addon-opt-${leg.serviceType}-${addon.id}`}
                  >
                    {/* label wraps only the checkbox + name so clicking the
                        text toggles it; the stepper sits outside as a sibling. */}
                    <label className="choice-main">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) =>
                          setQty(
                            leg.serviceType,
                            addon,
                            e.target.checked ? 1 : 0,
                          )
                        }
                        data-testid={`addon-check-${leg.serviceType}-${addon.id}`}
                      />
                      <span>
                        {en ? addon.name || addon.name_zh : addon.name_zh}{' '}
                        <span className="muted">
                          +{fmtTwd(addon.price_minor)}
                        </span>
                      </span>
                    </label>
                    {checked && (
                      <span className="qty">
                        <button
                          type="button"
                          className="qty-btn"
                          onClick={() =>
                            setQty(leg.serviceType, addon, Math.max(0, qty - 1))
                          }
                          aria-label={t('packages.decrease')}
                        >
                          −
                        </button>
                        <span
                          className="qty-val"
                          data-testid={`addon-qty-${leg.serviceType}-${addon.id}`}
                        >
                          {qty}
                        </span>
                        <button
                          type="button"
                          className="qty-btn"
                          onClick={() =>
                            setQty(leg.serviceType, addon, Math.min(20, qty + 1))
                          }
                          aria-label={t('packages.increase')}
                        >
                          +
                        </button>
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/** Sum of selected add-ons in minor units (tax-inclusive). */
export function addOnsSubtotalMinor(selections: AddOnSelection[]): number {
  return selections.reduce((sum, s) => sum + s.price_minor * s.quantity, 0)
}
